/**
 * Daily Matters — Microsoft Clarity measurement layer for the waitlist.
 *
 * Clarity itself is loaded by the Microsoft Clarity app embed (Theme settings → App embeds), which also
 * passes Shopify's cookie consent on. This module adds what Clarity cannot see on its own: custom events for
 * the sign-up funnel and tags to filter recordings by. Every Clarity call on the page lives here. The
 * sections and dm-waitlist.js only dispatch plain DOM events (dm:submit, dm:subscribed, dm:klaviyo-error,
 * dm:shopify-error, dm:survey-answer, dm:survey-done), so deleting this file and its script tag in
 * dm-signup.liquid removes the whole layer. The list of events and tags, and the question each one answers, is
 * in DAILY-MATTERS.md under "Meting in Microsoft Clarity"; keep the two in step.
 *
 * Privacy: no personal data in an event name or tag value. The e-mail address, the first name and the
 * survey's free-text answers are never read, and clarity('identify') is never called.
 *
 * Listeners are delegated on document, so they survive the theme editor re-rendering a section.
 */
(() => {
  // The merchant's own clicks in the theme editor are not visitor behaviour.
  if (window.Shopify && window.Shopify.designMode) return;

  const TAG_MAX_LENGTH = 255;
  // How long something has to stay on screen to count as seen: a fast scroll past, or the smooth scroll
  // after a sign-up button, does not.
  const SEEN_DWELL_MS = 1000;
  // Seen = at least this share of the element is visible, or it fills that share of the viewport (tall
  // sections). The form counts sooner: once its first fields are on screen the visitor has seen it.
  const SECTION_SEEN_SHARE = 0.5;
  const FORM_SEEN_SHARE = 0.25;
  const HONEYPOT = '[data-dm-honeypot]';
  const INVALID_EVENTS = {
    'contact[email]': 'signup_err_email',
    'contact[first_name]': 'signup_err_name',
  };

  const send = (...args) => {
    // The app embed defines window.clarity as a queue in <head>. Without it (embed off, script blocked) do nothing.
    if (typeof window.clarity !== 'function') return;
    try {
      window.clarity(...args);
    } catch (error) {
      // Measurement must never break the page that carries the sign-up.
      console.error('[dm-clarity]', error);
    }
  };

  const track = (name) => send('event', name);

  const tag = (key, value) => {
    const text = value == null ? '' : String(value).trim();
    if (text) send('set', key, text.slice(0, TAG_MAX_LENGTH));
  };

  // Clarity samples recordings once a project gets busy. Failed sign-ups are rare and are exactly the
  // sessions worth watching, so ask it to keep them.
  const upgrade = (reason) => send('upgrade', reason);

  const fired = new Set();
  /** For stages rather than actions: a visitor starts the form once, however many fields they touch. */
  const once = (name) => {
    if (fired.has(name)) return false;
    fired.add(name);
    return true;
  };

  const signupForm = () => document.querySelector('form[data-dm-signup-form]');

  /** Calls back once per element, after it has been seen for SEEN_DWELL_MS without interruption. */
  const whenSeen = (elements, share, callback) => {
    if (!elements.length || !('IntersectionObserver' in window)) return;
    const timers = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const element = entry.target;
          const visible = entry.intersectionRect.height;
          const needed = Math.min(entry.boundingClientRect.height, window.innerHeight) * share;
          if (!entry.isIntersecting || visible < needed) {
            window.clearTimeout(timers.get(element));
            timers.delete(element);
            return;
          }
          if (timers.has(element)) return;
          timers.set(
            element,
            window.setTimeout(() => {
              observer.unobserve(element);
              callback(element);
            }, SEEN_DWELL_MS)
          );
        });
      },
      // Fine-grained, because a section three screens tall never gets past a third of itself in view.
      { threshold: Array.from({ length: 21 }, (_, i) => i / 20) }
    );
    elements.forEach((element) => observer.observe(element));
  };

  /* 1. The page ---------------------------------------------------------------- */
  // page_version is the sign-up section's setting (also sent to Klaviyo as waitlist_page_version). Bump it
  // whenever the page changes, then every Clarity filter can split sessions into before and after.
  const form = signupForm();
  if (form) tag('page_version', form.dataset.pageVersion);

  // How far down the page a visitor really read: every section they stayed on for a second, by its key
  // in the template ("hero", "flavours", "offer"…). The sticky bar is always on screen, so it is left out.
  const sections = Array.from(document.querySelectorAll('#MainContent .shopify-section')).filter(
    (section) => section.id.includes('__') && !section.querySelector('[data-dm-sticky-bar]')
  );
  whenSeen(sections, SECTION_SEEN_SHARE, (section) => tag('section_seen', section.id.split('__').pop()));

  /* 2. The sign-up funnel ------------------------------------------------------- */
  // signup_seen → signup_start → signup_submit → waitlist_signup, with signup_err_* where it goes wrong.
  if (form) whenSeen([form], FORM_SEEN_SHARE, () => track('signup_seen'));

  const inSignupFields = (element) =>
    element instanceof Element &&
    element.matches('input, select, textarea') &&
    !element.matches(HONEYPOT) &&
    Boolean(element.closest('form[data-dm-signup-form] [data-dm-signup-fields]'));

  const startSignup = (event) => {
    if (inSignupFields(event.target) && once('signup_start')) track('signup_start');
  };
  document.addEventListener('focusin', startSignup);
  // Safari does not focus a radio button on click, so a flavour chip only shows up as a change.
  document.addEventListener('change', startSignup);

  // dm-waitlist.js holds the submit event from every other listener (Shopify's captcha would post the form
  // too early), so it signals each sign-up that got past the browser's validation instead.
  document.addEventListener('dm:submit', (event) => {
    track('signup_submit');
    // The hidden honeypot field had a value: a bot, or autofill (in October 2026 autofill in the Facebook and
    // Instagram in-app browsers on Android filled it for real visitors). It no longer blocks the sign-up, so
    // this tag is the only trace of it.
    if ((event.detail || {}).honeypot) {
      tag('honeypot', 'filled');
      upgrade('honeypot');
    }
  });

  // The browser's own validation stopped the submit (empty or malformed field). `invalid` does not
  // bubble, so listen in the capture phase.
  document.addEventListener(
    'invalid',
    (event) => {
      const field = event.target;
      const name = field instanceof Element ? INVALID_EVENTS[field.getAttribute('name')] : '';
      if (!name || !inSignupFields(field)) return;
      track(name);
      upgrade('signup_error');
    },
    true
  );

  document.addEventListener('dm:subscribed', (event) => {
    const { via, entryPoint, flavour } = event.detail || {};
    track('waitlist_signup');
    // Same value as waitlist_entry_point on the Klaviyo profile: the last sign-up button clicked, or "direct".
    tag('entry_point', entryPoint);
    // "klaviyo": Klaviyo took it before Shopify's post. "retry": Klaviyo took it only on the success page.
    // "shopify": Klaviyo failed twice, only Shopify has it (the Klaviyo flow on the Shopify tag catches it).
    tag('signup_path', via);
    tag('flavour', flavour);
  });

  document.addEventListener('dm:klaviyo-error', (event) => {
    const { endpoint, status } = event.detail || {};
    track(endpoint === 'subscription' ? 'signup_err_api' : 'survey_err_api');
    // An HTTP status, "timeout", or "network" when the request never got out (offline, ad blocker).
    tag('api_status', status);
    upgrade('signup_error');
  });

  // Shopify refused its post and sent the form back with an error; dm-waitlist.js put the sign-up back in the form,
  // or showed the success state when Klaviyo had it all the same (then waitlist_signup follows). No tag for the
  // reason: Shopify checks the fields it sends back, which lack the e-mail address, so its error nearly always says
  // the e-mail is empty.
  document.addEventListener('dm:shopify-error', () => {
    track('signup_err_shopify');
    upgrade('signup_error');
  });

  /* 3. After signing up: the survey and sharing ------------------------------------ */
  document.addEventListener('dm:survey-answer', (event) => {
    const { skipped, step } = event.detail || {};
    track(skipped ? 'survey_skip' : 'survey_answer');
    // The furthest question reached; its answers themselves live in Klaviyo.
    tag('survey_step', step);
  });

  document.addEventListener('dm:survey-done', (event) => {
    track('survey_done');
    tag('survey_answers', (event.detail || {}).answered);
  });

  /* 4. Clicks: sign-up buttons, accordions, share buttons ---------------------------- */
  /** A same-page link to the sign-up section that has no data-dm-cta of its own (a text link, say). */
  const leadsToSignup = (link) => {
    const current = signupForm();
    if (!current || !link.hash || link.pathname !== window.location.pathname) return false;
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    return Boolean(target && target.contains(current));
  };

  const text = (root, selector) => {
    const element = root.querySelector(selector);
    return element ? element.textContent : '';
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // One event plus a tag rather than an event per button: at this traffic five separate counts would
    // each be a handful of sessions, while the tag still narrows recordings down to one button.
    const cta = target.closest('[data-dm-cta]');
    const link = target.closest('a[href]');
    if (cta || (link && leadsToSignup(link))) {
      track('cta_click');
      tag('cta_source', (cta && cta.dataset.dmCta) || 'other');
      return;
    }

    const summary = target.closest('details.dm-acc__item > summary');
    if (summary) {
      const details = summary.parentElement;
      // The click comes before the toggle: an open item is about to close. Listening to `toggle` instead
      // would also count the items the page renders open.
      if (details.open) return;
      if (details.classList.contains('dm-ingredient')) {
        track('ingredient_open');
        tag('ingredient', text(details, '.dm-acc__title'));
      } else if (details.closest('.dm-faq')) {
        track('faq_open');
        tag('faq_q', text(details, '.dm-faq__question'));
      }
      return;
    }

    const share = target.closest('.dm-signup__share a[href], .dm-signup__share [data-dm-copy]');
    if (share) {
      track('share_click');
      let channel = 'whatsapp';
      if (share.hasAttribute('data-dm-copy')) channel = 'copy';
      else if (share.getAttribute('href').startsWith('mailto:')) channel = 'email';
      tag('share_channel', channel);
    }
  });
})();
